import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

// Wraps a wide table so its horizontal scrollbar sits ABOVE the table instead of at the
// bottom, and stays pinned below the fixed app header while the page scrolls. The top bar
// is a real overflow-x container holding a zero-height spacer as wide as the table, kept in
// two-way sync with the table's own scroll position.
const StickyScrollTable = ({ children, top = 66, className = "" }) => {
  const bodyRef = useRef(null);
  const barRef = useRef(null);
  // Guards the two onScroll handlers against re-entering each other and fighting over
  // scrollLeft (which shows up as jitter / a bar that won't reach the end).
  const syncing = useRef(false);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [overflowing, setOverflowing] = useState(false);

  const measure = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    setScrollWidth(el.scrollWidth);
    setOverflowing(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure, children]);

  const sync = (from, to) => () => {
    if (syncing.current) return;
    syncing.current = true;
    if (from.current && to.current) to.current.scrollLeft = from.current.scrollLeft;
    // Released on the next frame so the scroll event the assignment above fires on the
    // other element is swallowed rather than bouncing back.
    requestAnimationFrame(() => { syncing.current = false; });
  };

  return (
    <div className={`sticky-scroll-table ${className}`.trim()}>
      <div
        ref={barRef}
        className="sticky-scroll-table__bar"
        style={{ top, display: overflowing ? "block" : "none" }}
        onScroll={sync(barRef, bodyRef)}
      >
        <div style={{ width: scrollWidth, height: 1 }} />
      </div>
      <div ref={bodyRef} className="sticky-scroll-table__body" onScroll={sync(bodyRef, barRef)}>
        {children}
      </div>
    </div>
  );
};

StickyScrollTable.propTypes = {
  children: PropTypes.node,
  top: PropTypes.number,
  className: PropTypes.string,
};

export default StickyScrollTable;
